import { forwardRef, useState } from "react";
import { ArrowUpRight } from "lucide-react";

const SIZES = ["YS", "YM", "YL", "AS", "AM"];
const PRODUCTS = [
  { value: "jersey", label: "Volt Jersey" },
  { value: "tee", label: "BSE Tee" },
  { value: "both", label: "Both" },
];

const Reserve = forwardRef(function Reserve({ onSubmit }, ref) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    size: "YM",
    product: "jersey",
    number: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handle = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(form);
      setDone(true);
      setForm({ name: "", email: "", size: "YM", product: "jersey", number: "" });
      setTimeout(() => setDone(false), 4000);
    } catch (e) {
      // toast handled upstream
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="reserve"
      ref={ref}
      data-testid="reserve-section"
      className="relative bg-[#0a0a0a] py-24 md:py-32 border-t border-neutral-900"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
        {/* Left: copy */}
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 text-[10px] tracking-[0.3em] uppercase text-[#e11d2a] mb-5">
            <span className="inline-block w-8 h-px bg-[#e11d2a]" />
            <span>Reserve · Limited Run</span>
          </div>
          <h2 className="font-anton uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.9] tracking-tight">
            Lock in <br />
            <span className="text-[#e11d2a]">your size.</span>
          </h2>
          <p className="text-neutral-400 mt-8 text-base md:text-lg leading-relaxed max-w-md">
            We're doing one team run for the spring season. Drop your details
            and we'll lock in your size, number, and ship date. No payment yet
            — we'll send a secure checkout when production starts.
          </p>

          <div className="mt-10 space-y-4 text-sm">
            <Row label="Production Window" value="Early March 2026" />
            <Row label="Ships By" value="Pre-Season Practice" />
            <Row label="Reservation Fee" value="$0 · Pay on confirm" />
          </div>
        </div>

        {/* Right: form */}
        <div className="md:col-span-6 md:col-start-7">
          <form
            onSubmit={handle}
            data-testid="reserve-form"
            className="bg-[#0f0f0f] border border-neutral-900 p-8 md:p-12"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
              <Field
                label="Player Name"
                value={form.name}
                onChange={(v) => update("name", v)}
                placeholder="First & Last"
                testid="reserve-input-name"
                required
              />
              <Field
                label="Parent Email"
                type="email"
                value={form.email}
                onChange={(v) => update("email", v)}
                placeholder="you@example.com"
                testid="reserve-input-email"
                required
              />

              {/* Product */}
              <div className="md:col-span-2 mt-6">
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-bold mb-3">
                  Pick Your Drop
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {PRODUCTS.map((p) => (
                    <button
                      type="button"
                      key={p.value}
                      data-testid={`reserve-product-${p.value}`}
                      onClick={() => update("product", p.value)}
                      className={`py-3 text-xs font-bold uppercase tracking-wide border transition-all ${
                        form.product === p.value
                          ? "border-[#e11d2a] text-white bg-[#e11d2a]"
                          : "border-neutral-800 text-neutral-300 hover:border-neutral-600"
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Size */}
              <div className="md:col-span-2 mt-6">
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-bold mb-3">
                  Size
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {SIZES.map((s) => (
                    <button
                      type="button"
                      key={s}
                      data-testid={`reserve-size-${s}`}
                      onClick={() => update("size", s)}
                      className={`py-4 font-anton text-xl uppercase tracking-tight border transition-all ${
                        form.size === s
                          ? "border-[#e11d2a] text-white bg-[#e11d2a]"
                          : "border-neutral-800 text-neutral-300 hover:border-neutral-600"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-600 mt-2">
                  Y = Youth · A = Adult
                </div>
              </div>

              <Field
                label="Preferred Number"
                value={form.number}
                onChange={(v) => update("number", v.replace(/\D/g, "").slice(0, 2))}
                placeholder="00"
                testid="reserve-input-number"
                className="md:col-span-2 mt-4"
              />
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <p className="text-[10px] tracking-[0.25em] uppercase text-neutral-600 max-w-xs">
                By reserving you agree to be contacted about sizing & pickup.
              </p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="reserve-submit-btn"
                className="group inline-flex items-center gap-3 bg-[#e11d2a] hover:bg-[#ff3340] disabled:bg-neutral-700 disabled:text-neutral-400 text-white font-bold uppercase tracking-wide text-sm px-6 py-4 transition-all"
              >
                {submitting ? "Locking In..." : done ? "Locked In ✓" : "Secure Your Spot"}
                {!submitting && !done && (
                  <ArrowUpRight
                    size={18}
                    className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                  />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
});

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-neutral-900 pb-3">
      <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">
        {label}
      </span>
      <span className="font-anton text-lg uppercase tracking-tight">{value}</span>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <div className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-bold mt-4">
        {label}
        {required && <span className="text-[#e11d2a] ml-1">*</span>}
      </div>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        data-testid={testid}
        className="bse-input"
      />
    </label>
  );
}

export default Reserve;
